import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { AuthShell, Field, inputCls, SubmitBtn } from "@/components/student/AuthShell";
import { PasswordInput } from "@/components/student/PasswordInput";
import { apiError } from "@/lib/student-auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StudentResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) return setError("Passwords do not match");
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/student/reset-password`, { token, password: form.password });
      toast.success("Password updated — please log in");
      nav("/student/login");
    } catch (err) {
      setError(apiError(err?.response?.data?.detail, "Could not reset your password"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      testid="student-reset-page"
      eyebrow="Password reset"
      title="Set a new password."
      subtitle="Choose something you haven't used before — minimum 8 characters."
      footer={
        <Link to="/student/login" data-testid="link-back-login" className="text-[#06B6D4] font-medium">
          Back to login
        </Link>
      }
    >
      {!token ? (
        <div data-testid="reset-no-token" className="text-sm text-slate-300">
          This reset link is incomplete. Please request a new one from the{" "}
          <Link to="/student/forgot-password" className="text-[#06B6D4]">
            forgot password
          </Link>{" "}
          page.
        </div>
      ) : (
        <form onSubmit={submit} data-testid="student-reset-form">
          <Field label="New Password">
            <PasswordInput
              testid="reset-password"
              required
              minLength={8}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className={inputCls}
              placeholder="••••••••"
            />
          </Field>
          <Field label="Confirm New Password">
            <PasswordInput
              testid="reset-confirm"
              required
              value={form.confirm}
              onChange={(e) => setForm({ ...form, confirm: e.target.value })}
              className={inputCls}
              placeholder="••••••••"
            />
          </Field>
          {error && (
            <div data-testid="reset-error" className="mb-4 rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-xs text-rose-200">
              {error}
            </div>
          )}
          <SubmitBtn loading={loading} testid="reset-submit">
            Update password
          </SubmitBtn>
        </form>
      )}
    </AuthShell>
  );
}
